import { Badge, Divider, Flex, Heading, Tag, Text } from '@chakra-ui/react';
import { inquiryApi } from '@texas/api/endpoints/inquiryApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  MetadataList,
  MetadataListRow,
} from '@texas/components/shared/MetadataList';
import { NoData } from '@texas/components/shared/NoData';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  inquiryId: number;
}

export function InquiryHoverCard({ inquiryId }: Props) {
  const { t } = useTranslation();
  const { data, refetch, loading } = useApiResource(inquiryApi.getInquiry);
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();

  useEffect(() => {
    refetch(inquiryId);
  }, [inquiryId, refetch]);

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  if (loading) return <SpinnerWithLabel label={t('general.loading')} />;
  if (!data) return <NoData label={t('inquiry.noInquiry')} />;

  const hasCharges =
    data.sampleCharge > 0 || data.mouldCharge > 0 || data.surcharge > 0;
  const currencyCode = findCurrency(data.currencyId)?.code ?? '';

  return (
    <Flex flexDir="column" gap={2} minW="240px">
      <Flex alignItems="center" gap={2}>
        <Heading fontSize="md">{data.formattedInquiryNumber}</Heading>
        {data.acceptedDate ? (
          <Badge colorScheme="green">{t('inquiry.accepted')}</Badge>
        ) : (
          <Badge colorScheme="gray">{t('inquiry.inquiry')}</Badge>
        )}
        {data.archived && (
          <Badge colorScheme="orange">{t('general.archived')}</Badge>
        )}
      </Flex>

      <MetadataList>
        <MetadataListRow label={t('general.supplier')}>
          <Text>{data.supplierName}</Text>
        </MetadataListRow>
        {data.mpqQuantity != null && (
          <MetadataListRow label={t('inquiry.mpq')}>
            <Text>{data.mpqQuantity}</Text>
          </MetadataListRow>
        )}
        {hasCharges && (
          <>
            {data.sampleCharge > 0 && (
              <MetadataListRow label={t('inquiry.sampleCharge')}>
                <Text>{data.sampleCharge}</Text>
              </MetadataListRow>
            )}
            {data.mouldCharge > 0 && (
              <MetadataListRow label={t('inquiry.mouldCharge')}>
                <Text>{data.mouldCharge}</Text>
              </MetadataListRow>
            )}
            {data.surcharge > 0 && (
              <MetadataListRow label={t('inquiry.surcharge')}>
                <Text>{data.surcharge}</Text>
              </MetadataListRow>
            )}
          </>
        )}
      </MetadataList>

      {data.tierPrices.length > 0 && (
        <>
          <Divider />
          <Text fontSize="xs" fontWeight="semibold" color="gray.400">
            {t('inquiry.tierPrices')}
          </Text>
          <Flex direction="column" gap={1}>
            {[...data.tierPrices]
              .sort((a, b) => a.quantity - b.quantity)
              .map((tier) => (
                <Flex
                  key={tier.id}
                  justify="space-between"
                  align="center"
                  gap={4}
                >
                  <Tag size="sm" colorScheme="gray">
                    {tier.quantity}
                  </Tag>
                  <Text fontSize="sm">
                    {tier.price != null
                      ? `${tier.price.toFixed(
                          data.decimalsInPrice,
                        )} ${currencyCode}`
                      : '—'}
                  </Text>
                </Flex>
              ))}
          </Flex>
        </>
      )}
    </Flex>
  );
}
